import React, { ChangeEvent, useMemo } from 'react'
import { GameZones, WtnColorsEnum } from 'types/global'
import { ClassNamesProps } from 'types/utilities'
import { InternalState } from './types'
import withInfoModal from 'components/generic/hoc/withInfoModal'
import cx from 'classnames'
import Separator from 'components/generic/Separator'
import CheckboxWTN from 'components/generic/CheckboxWTN'
import GameZoneModalContent from './GameZoneModalContent'
import { WALK_UP_REG_FULL_URL } from 'constants/global'
import { Range } from 'rc-slider'
import RangeInputs from '../RangeInputs'
import { RatingTypeEnum } from 'types/api/enums'
import ConfidenceModalContent from './ConfidenceModalContent'
import { useTranslation } from 'react-i18next'
import dropdownStyles from '../styles/dropdown.module.scss'
import styles from './combinedRatingFilter.module.scss'

type ClassKeys =
  | 'sliderWrapper'
  | 'slider'
  | 'groupLabel'
  | 'radio'
  | 'radioLabel'
  | 'radioGroup'
  | 'separator'
  | 'inputsContainer'
  | 'input'
  | 'inputGroup'
  | 'inputLabel'

interface Props extends ClassNamesProps<ClassKeys> {
  id: string
  minRange: number
  maxRange: number
  stepSize: number
  gameZone?: GameZones
  internalState: InternalState
  setInternalState: React.Dispatch<React.SetStateAction<InternalState>>
  handleInputBlur: () => void
  handleMinInputChange: (e: ChangeEvent) => void
  handleMaxInputChange: (e: ChangeEvent) => void
}

const CombinedRatingFilter = ({
  id,
  minRange,
  maxRange,
  stepSize,
  gameZone,
  handleInputBlur,
  handleMinInputChange,
  handleMaxInputChange,
  internalState,
  setInternalState,
  classNames,
}: Props) => {
  const { t } = useTranslation(['strCommonUI', 'strFilters'])
  const { error, range, inputsRange, canApply, isGameZoneApplied, type } = internalState
  const gameZoneBounds = useMemo(() => gameZone && gameZone[type], [gameZone, type])

  const rangeValues = useMemo(() => {
    if (isGameZoneApplied && gameZoneBounds) return [gameZoneBounds.upper, gameZoneBounds.lower]
    return [range.min ?? minRange, range.max ?? maxRange]
  }, [range, minRange, maxRange, isGameZoneApplied, gameZoneBounds])

  const rangeInputsValues = useMemo(() => {
    if (isGameZoneApplied && gameZoneBounds)
      return { min: gameZoneBounds.upper, max: gameZoneBounds.lower }
    return inputsRange
  }, [isGameZoneApplied, gameZoneBounds, inputsRange])

  const ConfidenceLevelLabel = useMemo(
    () =>
      withInfoModal('confidence-level-info-modal', () => (
        <label
          htmlFor={`confidenceLevel-${id}`}
          className={cx(styles.groupLabel, classNames?.groupLabel)}
        >
          {t('rating.labelConfidenceLevel', { ns: 'strFilters' })}
        </label>
      )),
    [id, t, classNames?.groupLabel]
  )

  const GameZoneLabel = useMemo(
    () =>
      withInfoModal('game-zone-info-modal', () => (
        <span className={cx(styles.gameZoneLabel)}>
          {t('rating.labelGameZone', { ns: 'strFilters' })}
        </span>
      )),
    [t]
  )

  return (
    <>
      <div
        role='group'
        className={cx(styles.radioGroup, styles.labelLessGroup, classNames?.radioGroup)}
      >
        <label
          htmlFor={`toggleAllPlayers-${id}`}
          className={cx(styles.commonRadioLabel, styles.radioLabel, classNames?.radioLabel)}
        >
          <input
            id={`toggleAllPlayers-${id}`}
            type='radio'
            name='show-range'
            value=''
            onChange={e => {
              e.persist()
              setInternalState(prev => ({ ...prev, canApply: !e.target.checked }))
            }}
            checked={!canApply}
            className={cx(classNames?.radio)}
          />{' '}
          {t('rating.radioAllPlayers', { ns: 'strFilters' })}
        </label>
        <label
          htmlFor={`toggleRatingRange-${id}`}
          className={cx(styles.commonRadioLabel, styles.radioLabel, classNames?.radioLabel)}
        >
          <input
            id={`toggleRatingRange-${id}`}
            type='radio'
            name='show-range'
            value='range'
            onChange={e => {
              e.persist()
              setInternalState(prev => ({ ...prev, canApply: e.target.checked }))
            }}
            checked={canApply}
            className={cx(classNames?.radio)}
          />{' '}
          {t('rating.radioRatingRange', { ns: 'strFilters' })}
        </label>
      </div>

      {canApply && (
        <>
          <Separator className={cx(styles.separator, classNames?.separator)} />
          <div>
            <CheckboxWTN
              className={styles.gameZoneFilter}
              isDisabled={!gameZoneBounds}
              isChecked={isGameZoneApplied}
              onChange={e => {
                setInternalState(prev => ({ ...prev, isGameZoneApplied: e.target.checked }))
              }}
            >
              <GameZoneLabel
                wrapperClass={styles.infoModalGameZoneContainer}
                modalWrapperClass={cx(styles.infoModalGameZone, styles.override)}
                widgetElId='player-filter-bar'
                modalHeaderText={t('gameZoneModal.title', { ns: 'strFilters' })}
                modalAcceptBtnText={t('button.close', { ns: 'strCommonUI' })}
                iconColor={WtnColorsEnum.PRIMARY_ALT}
              >
                <GameZoneModalContent registerUrl={WALK_UP_REG_FULL_URL} />
              </GameZoneLabel>
            </CheckboxWTN>
            <div className={cx(styles.rangeWrapper, classNames?.sliderWrapper)}>
              <Range
                reverse
                allowCross={false}
                className={cx(dropdownStyles.slider, classNames?.slider, {
                  '--is-disabled': isGameZoneApplied,
                })}
                min={minRange}
                max={maxRange}
                value={rangeValues}
                step={stepSize}
                defaultValue={rangeValues}
                onChange={([min, max]) => {
                  if (max - min > 0) {
                    setInternalState(prev => ({
                      ...prev,
                      range: { min, max },
                      inputsRange: { min, max },
                      error: null,
                    }))
                  }
                }}
                disabled={isGameZoneApplied}
              />
            </div>
            <RangeInputs
              id={id}
              min={rangeInputsValues.min}
              max={rangeInputsValues.max}
              inputError={error}
              handleMaxInputChange={handleMaxInputChange}
              handleMinInputChange={handleMinInputChange}
              onBlur={handleInputBlur}
              classNames={{
                inputsContainer: cx(styles.rangeInputsContainer, classNames?.inputsContainer),
                input: classNames?.input,
                inputGroup: classNames?.inputGroup,
                label: classNames?.inputLabel,
              }}
              disabled={isGameZoneApplied}
            />
          </div>
          <Separator className={cx(styles.separator, classNames?.separator)} />
          <label
            htmlFor={`ratingType-${id}`}
            className={cx(styles.groupLabel, classNames?.groupLabel)}
          >
            {t('rating.labelRatingType', { ns: 'strFilters' })}
          </label>
          <div
            id={`ratingType-${id}`}
            role='group'
            className={cx(styles.radioGroup, classNames?.radioGroup)}
          >
            <label
              htmlFor={`ratingType-singles-${id}`}
              className={cx(styles.commonRadioLabel, styles.radioLabel, classNames?.radioLabel)}
            >
              <input
                id={`ratingType-singles-${id}`}
                type='radio'
                name='rating-type'
                value='singles'
                onChange={e => {
                  e.persist()
                  if (e.target.checked)
                    setInternalState(prev => ({
                      ...prev,
                      type: RatingTypeEnum.ITF,
                      isGameZoneApplied: false,
                    }))
                }}
                checked={internalState.type === RatingTypeEnum.ITF}
                className={cx(classNames?.radio)}
              />{' '}
              {t('rating.radioSingles', { ns: 'strFilters' })}
            </label>
            <label
              htmlFor={`ratingType-doubles-${id}`}
              className={cx(styles.commonRadioLabel, styles.radioLabel, classNames?.radioLabel)}
            >
              <input
                id={`ratingType-doubles-${id}`}
                type='radio'
                name='rating-type'
                value='doubles'
                onChange={e => {
                  e.persist()
                  if (e.target.checked)
                    setInternalState(prev => ({
                      ...prev,
                      type: RatingTypeEnum.ITFDBL,
                      isGameZoneApplied: false,
                    }))
                }}
                checked={internalState.type === RatingTypeEnum.ITFDBL}
                className={cx(classNames?.radio)}
              />{' '}
              {t('rating.radioDoubles', { ns: 'strFilters' })}
            </label>
          </div>
          <Separator className={cx(styles.separator, classNames?.separator)} />
          <ConfidenceLevelLabel
            wrapperClass={styles.infoModalLabelContainer}
            modalWrapperClass={cx(styles.infoModalConfidence, styles.override)}
            widgetElId='player-filter-bar'
            modalHeaderText={t('confidenceModal.title', { ns: 'strFilters' })}
            modalAcceptBtnText={t('button.close', { ns: 'strCommonUI' })}
            iconColor={WtnColorsEnum.PRIMARY_ALT}
          >
            <ConfidenceModalContent />
          </ConfidenceLevelLabel>
          <div
            id={`confidenceLevel-${id}`}
            role='group'
            className={cx(styles.radioGroup, classNames?.radioGroup)}
          >
            <label
              htmlFor={`confidenceLevel-high-${id}`}
              className={cx(styles.commonRadioLabel, styles.radioLabel, classNames?.radioLabel)}
            >
              <input
                id={`confidenceLevel-high-${id}`}
                type='radio'
                name='confidenceLevel'
                value='high'
                onChange={e => {
                  e.persist()
                  setInternalState(prev => ({ ...prev, confidence: e.target.checked }))
                }}
                checked={internalState.confidence}
                className={cx(classNames?.radio)}
              />{' '}
              {t('rating.radioConfidenceHigh', { ns: 'strFilters' })}
            </label>
            <label
              htmlFor={`confidenceLevel-all-${id}`}
              className={cx(styles.commonRadioLabel, styles.radioLabel, classNames?.radioLabel)}
            >
              <input
                id={`confidenceLevel-all-${id}`}
                type='radio'
                name='confidenceLevel'
                value=''
                onChange={e => {
                  e.persist()
                  setInternalState(prev => ({ ...prev, confidence: !e.target.checked }))
                }}
                checked={!internalState.confidence}
                className={cx(classNames?.radio)}
              />{' '}
              {t('rating.radioConfidenceAll', { ns: 'strFilters' })}
            </label>
          </div>
        </>
      )}
    </>
  )
}

export default CombinedRatingFilter
